// #region Global Imports
import * as React from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import dynamic from 'next/dynamic';
import type { NextPage } from 'next/types';
import { useQuery } from '@apollo/client';
import type { IPages } from 'types';
// #endregion Global Imports
// #region Local Imports
import { Ads } from '@components/Adition/Ads';
import { Authors } from '@components/Authors';
import { Layout } from '@components/Layout';
import type { TermLoginFormFragment } from '@components/LoginForm';
import { LoginForm } from '@components/LoginForm';
import { PostContent, PostContentFragment } from '@components/PostContent';
import styles from '@components/PostContent/PostContent.module.sass';
import type {
  PostAfterMetaFragment,
  PostMetaFragment,
} from '@components/PostContent/Meta';
import { PostAfterMeta, PostMeta } from '@components/PostContent/Meta';
import { PDF, Print } from '@components/PostContent/Tools/Tools';
import { WrapperOtherPosts } from '@components/PostContent/Wrapper/WrapperOtherPosts';
import type { PostTeasersFragment } from '@components/PostTeasers';
import * as Wrapper from '@components/Wrapper';
import { AuthContext } from '@contexts/AuthContext';
import {
  addApolloState,
  initializeApollo,
  withDefaultVariables,
} from '@services/apollo';
import {
  PostQueryDocument,
  RelatedPostsQueryDocument,
} from '@generated/graphql';
import { renderBlockContentTranslation } from '@lib/func/renderBlockContentTranslation';
import type { FragmentType } from '@generated/fragment-masking';
import { getFragmentData } from '@generated/fragment-masking';
import type { AuthorsFragment } from '@components/Authors/Authors';
import { CategoriesFragment, TagsFragment } from '@components/Taxonomies';
import { DatelineFragment } from '@components/Taxonomies/Dateline';
import { withSession } from '@lib/auth';
import type { TermTocFragment } from '@components/PostContent/Toc';
import { Toc } from '@components/PostContent/Toc';
import { Spinner } from '@components/Spinner';
import { RelatedPostsFragment } from '@components/Post/graphql';
import { prisma } from '@lib/prisma';
// #endregion Local Imports

const PostTeasers = dynamic(
  () => import('@components/PostTeasers').then((mod) => mod.PostTeasers),
  {
    loading: () => <Spinner />,
    ssr: false,
  },
);

export const Post: NextPage<IPages.Post> = ({
  variables,
  relatedQueryVariables,
  user,
  language,
}) => {
  const { data, loading } = useQuery(PostQueryDocument, {
    variables,
  });

  const { data: relatedPostsData } = useQuery(RelatedPostsQueryDocument, {
    variables: relatedQueryVariables,
  });

  if (loading) return <Spinner />;

  const entity = getFragmentData(
    PostContentFragment,
    get(data, ['route', 'entity']),
  );

  const relatedPosts = getFragmentData(
    RelatedPostsFragment,
    get(relatedPostsData, ['route', 'entity']),
  );

  return (
    <div className={styles.Post__root}>
      <Layout
        data={data}
        language={language}
        title={entity.title ?? ''}
        user={user ?? {}}
      >
        <AuthContext.Consumer>
          {(ctx): React.ReactElement => (
            <>
              <Wrapper.WrapperContentFullWidth className={styles.Post__head}>
                <PostMeta
                  postMeta={entity as FragmentType<typeof PostMetaFragment>}
                  termIssue={
                    renderBlockContentTranslation(data!.termIssue) as string
                  }
                />
              </Wrapper.WrapperContentFullWidth>
              <Wrapper.WrapperContent>
                <PostContent
                  _propperEnable={false}
                  postContentEntity={
                    entity as FragmentType<typeof PostContentFragment>
                  }
                />
                {!ctx.token && (
                  <div className={styles.Post__LoginForm}>
                    <LoginForm
                      language={language}
                      termLoginFormData={
                        data as FragmentType<typeof TermLoginFormFragment>
                      }
                    />
                  </div>
                )}
                <PostAfterMeta
                  postAfterMeta={
                    entity as FragmentType<typeof PostAfterMetaFragment>
                  }
                />
              </Wrapper.WrapperContent>
              <Wrapper.WrapperContentMargin>
                <Wrapper.WrapperMargin>
                  <Wrapper.WrapperMarginLeft
                    className={styles.Post__additions_tools}
                  >
                    <div className={styles.Post__AuthorsPrint_sidebar}>
                      {ctx.token ? (
                        <>
                          <Print
                            termPrint={renderBlockContentTranslation(data?.termPrint)}
                          />
                          <PDF />
                        </>
                      ) : null}
                      {!isEmpty(entity.authors) ? (
                        <Authors
                          entity={
                            entity as FragmentType<typeof AuthorsFragment>
                          }
                        />
                      ) : null}
                    </div>
                    <Toc data={data as FragmentType<typeof TermTocFragment>} />
                  </Wrapper.WrapperMarginLeft>
                  <Wrapper.WrapperMarginRight>
                    <Ads />
                  </Wrapper.WrapperMarginRight>
                </Wrapper.WrapperMargin>
              </Wrapper.WrapperContentMargin>
              <WrapperOtherPosts>
                {!isEmpty(relatedPosts.entityQueryExclusive.entities) ? (
                  <>
                    <h2>
                      {renderBlockContentTranslation(
                        data?.termMoreContributions,
                        'More Content',
                      )}
                    </h2>
                    <PostTeasers
                      entityQueryResult={
                        {
                          ...relatedPosts.entityQueryExclusive.entities,
                          entities:
                            relatedPosts.entityQueryExclusive.entities?.slice(
                              0,
                              3,
                            ), // max: 3
                        } as FragmentType<typeof PostTeasersFragment>
                      }
                      termIssue=""
                    />
                  </>
                ) : null}
              </WrapperOtherPosts>
            </>
          )}
        </AuthContext.Consumer>
      </Layout>
    </div>
  );
};

export const getServerSideProps = withSession(async (context) => {
  const apolloClient = initializeApollo(null, context.req.jwt);

  const variables = withDefaultVariables(context, {});

  const { data, error } = await apolloClient.query({
    query: PostQueryDocument,
    variables,
  });

  if (!data.route || error) {
    return {
      notFound: true,
    };
  }

  const entity = getFragmentData(
    PostContentFragment,
    get(data, ['route', 'entity']),
  );

  const { locale } = context;
  const uri = context.resolvedUrl;

  const post = await prisma.nodeCount.findFirst({
    where: { uri },
  });

  try {
    if (isEmpty(post)) {
      await prisma.nodeCount.create({
        data: {
          title:
            entity.title ??
            // For now this is the default value.
            'Ausgleich der kalten Progression: EFD passt Tarife und Abzüge an',
          uri,
          locale: locale ?? 'de',
          count: 1,
        },
      });
    } else {
      await prisma.nodeCount.update({
        where: {
          title: post.title,
        },
        data: {
          ...post,
          count: (post.count || 1) + 1,
        },
      });
    }
  } catch {
    // Skip Error 500 ...
  }

  const tags = getFragmentData(
    TagsFragment,
    entity as FragmentType<typeof TagsFragment>,
  ).tags;

  const categories = getFragmentData(
    CategoriesFragment,
    entity as FragmentType<typeof CategoriesFragment>,
  ).categories;

  const dateline = getFragmentData(
    DatelineFragment,
    entity as FragmentType<typeof DatelineFragment>,
  ).dateline;

  const relatedQueryVariables = withDefaultVariables(context, {
    tags: tags?.map((tag) => get(tag, ['id'])).join(),
    categories: categories?.map((category) => get(category, ['id'])).join(),
    dateline: dateline?.map((date) => get(date, ['id'])).join(),
  });

  await apolloClient.query({
    query: RelatedPostsQueryDocument,
    variables: relatedQueryVariables,
  });

  return addApolloState(context, apolloClient, {
    props: {
      variables,
      relatedQueryVariables,
    },
  });
});

export default Post;
